import React from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronLeft } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { isSidebarRoute as isAppSidebarRoute } from '@/utils/appRoutes';
import { useBackNavigation } from '@/hooks/useBackNavigation';
import { Agent } from '@/types/Agents';
import { useTranslation } from 'react-i18next';

interface HeaderProps {
  agent: Agent | null | undefined;
}

const Header: React.FC<HeaderProps> = ({ agent }) => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const handleBack = useBackNavigation();

  // Routes available in the Sidebar - hide back button on these
  const isSidebarRoute = isAppSidebarRoute(location.pathname);

  // Don't render the header if we're on a sidebar route
  if (isSidebarRoute) {
    return null;
  }

  // Get page title based on route
  const getPageTitle = (): string | null => {
    if (agent && location.pathname.startsWith(`/agents/${agent.id}`)) {
      return agent.name;
    }
    if (location.pathname === '/agents/new') {
      return t('agent.list.new_agent.title.translation', 'New Agent');
    }
    if (location.pathname === '/change-password') {
      return t('change_password.form.submit.translation', 'Change Password');
    }
    if (location.pathname === '/help/forwarding') {
      return t(
        'help.call_forwarding.title.translation',
        'Call forwarding in Latvia'
      );
    }
    return null;
  };

  const pageTitle = getPageTitle();
  const showTitle = pageTitle !== null;

  return (
    <header className="bg-background text-foreground p-3 lg:p-5 transition-all duration-300">
      <div className="px-2 lg:px-4 flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBack}
          className="text-foreground hover:text-foreground/80 p-2 h-auto"
        >
          <ChevronLeft className="h-5 w-5" />
          {!isMobile && (
            <span className="ml-2">{t('common.back.translation')}</span>
          )}
        </Button>

        {/* Show page title in the middle */}
        {showTitle && (
          <div className="flex-1 text-center">
            <h1 className="text-lg font-semibold text-foreground truncate max-w-xs mx-auto">
              {pageTitle}
            </h1>
          </div>
        )}

        {/* Spacer to maintain layout when title is shown */}
        {showTitle && <div className="w-[88px]" />}
      </div>
    </header>
  );
};

export default Header;
